import React, { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { SendRounded, Close } from '@mui/icons-material';
import styled from 'styled-components';
import { Main } from 'styles/themeColors';
import { STAGE } from 'constants/aiVariantImport';
import { TEXT_LABELS } from 'constants/productDetails';
import Variant from 'data/Variant';
import { Color, FontSize, FontWeight } from 'lib/styles';
import VariantErrorCount from 'common/VariantErrorCount';

const PromptContainer = styled.div`
  border-radius: 0.75rem;
  padding: 20px;
`;

const PromptHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const PromptTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const PromptFieldWrapper = styled.div`
  width: 70%;
  margin: 0 auto;
`;

const TextareaWrapper = styled.div<{ hasChips: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 2px solid ${Main.brandLight};
  border-radius: ${props => props.hasChips ? '16px' : '32px'};
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-color: ${Main.brandLight};
  }
`;

const PromptTextarea = styled.textarea<{ hasChips: boolean }>`
  width: 100%;
  min-height: ${props => props.hasChips ? '40px' : '56px'};
  padding: ${props => props.hasChips ? '0.5rem 3.75rem 0.75rem 1rem' : '1rem 3.75rem 1rem 1.25rem'};
  background-color: transparent;
  border: none;
  border-radius: 0 0 16px 16px;
  font-size: 1rem;
  line-height: 1.5;
  resize: none;
  outline: none;
  overflow-y: auto;
  max-height: 200px;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const InlineGenerateButton = styled.button<{ isExpanded: boolean }>`
  position: absolute;
  right: 10px;
  bottom: 8px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Main.brandLight};
  color: white;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  pointer-events: auto;
  margin: 0;
  padding: 0;

  &:disabled {
    background-color: ${Main.brandLight};
  }

  &:hover:not(:disabled) {
    background-color: ${Main.brandDark};
  }
`;

const PromptActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const CharacterCount = styled.span<{ isNearLimit: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.isNearLimit ? '#ef4444' : '#6b7280'};
  margin-left: auto;
`;

const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 1rem 0.25rem 1rem;
  max-height: 120px;
  overflow-y: auto;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const InputRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VariantChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.5rem 0.375rem 0.75rem;
  background-color: ${Color.carrotOrange};
  border: 1px solid ${Color.lightGray};
  border-radius: 14px;
  color: white;
  max-width: 200px;
  font-size:  ${FontSize.small};
  font-weight: ${FontWeight.medium};
`;

const ChipText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: white;
  transition: background-color 0.2s;
`;

interface Props {
  prompt: string;
  setPrompt: (value: string) => void;
  examplePrompts: string[];
  onSubmit: () => void;
  stage: string;
  disabled?: boolean;
  selectedVariants?: number[];
  productVariants?: Variant[];
  onRemoveVariant?: (variantId: number) => void;
  variantErrors?: any[];
}

const PromptInput: React.FC<Props> = ({
  prompt,
  setPrompt,
  examplePrompts,
  onSubmit,
  stage,
  disabled,
  selectedVariants = [],
  productVariants = [],
  onRemoveVariant,
  variantErrors = []
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const getSelectedVariantDetails = () => {
    return selectedVariants
      .map(id => productVariants.find(v => v.id === id))
      .filter((v): v is Variant => v !== undefined);
  };
  const MAX_LENGTH = 1000;

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    // Reset height to auto to get accurate scrollHeight measurement
    el.style.height = 'auto';

    if (!prompt.trim()) {
      const baseHeight = selectedVariants.length > 0 ? 40 : 56;
      el.style.height = `${baseHeight}px`;
      setIsExpanded(false);
      return;
    }

    const next = Math.min(el.scrollHeight, 240);
    el.style.height = `${next}px`;
    setIsExpanded(next > 56);
  }, [prompt, selectedVariants.length]);

  useLayoutEffect(() => {
    adjustHeight();
  }, [adjustHeight]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_LENGTH) {
      setPrompt(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (prompt.trim().length > 0 && stage !== STAGE.PROCESSING) {
        onSubmit();
      }
    } else if (['ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(e.key)) {
      // Adjust height when navigating with arrow keys or other navigation keys
      requestAnimationFrame(adjustHeight);
    }
  };

  return (
    <PromptContainer>
      <PromptHeader style={{ justifyContent: 'center' }}>
        <PromptTitle
          style={{
            fontWeight: 700,
            background: 'linear-gradient(90deg, #fc780b, #ffae42)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center',
            width: '100%',
            margin: 0
          }}
        >
          {TEXT_LABELS.SMART_VARIANTS_PROMPT_TITLE}
        </PromptTitle>
      </PromptHeader>

      <PromptFieldWrapper>
        <TextareaWrapper hasChips={selectedVariants.length > 0}>
          {selectedVariants.length > 0 && (
            <ChipsContainer>
              {getSelectedVariantDetails().map(variant => (
                <VariantChip key={variant.id}>
                  <ChipText title={variant.sku || `Variant ${variant.id}`}>
                    {variant.sku || `Variant ${variant.id}`}
                  </ChipText>
                  {onRemoveVariant && (
                    <ChipCloseButton
                      onClick={() => onRemoveVariant(variant.id!)}
                      aria-label={`Remove ${variant.sku || variant.id}`}
                    >
                      <Close style={{ fontSize: '14px' }} />
                    </ChipCloseButton>
                  )}
                </VariantChip>
              ))}
            </ChipsContainer>
          )}
          <InputRow>
            <PromptTextarea
              ref={textareaRef}
              value={prompt}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={TEXT_LABELS.SMART_VARIANTS_PLACEHOLDER}
              maxLength={MAX_LENGTH}
              hasChips={selectedVariants.length > 0}
            />
            {prompt.trim().length > 0 && (
              <InlineGenerateButton onClick={onSubmit} aria-label="Generate" isExpanded={isExpanded} disabled={stage === STAGE.PROCESSING || disabled}>
                <SendRounded style={{ fontSize: '1.125rem' }} />
              </InlineGenerateButton>
            )}
          </InputRow>
        </TextareaWrapper>
        <PromptActions style={{ marginTop: 20, justifyContent: 'space-between', position: 'relative' }}>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <VariantErrorCount
              errors={variantErrors}
              visible={variantErrors?.length > 0}
              productRedesignFlag={true}
            />
          </div>
          <div style={{ fontWeight: 'lighter', fontSize: '12px', color: '#70777E', textAlign: 'center', flex: 1 }}>
            {TEXT_LABELS.AI_CAN_MAKE_MISTAKES}
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            {!disabled && (
              <CharacterCount isNearLimit={prompt.length >= MAX_LENGTH * 0.9}>
                {prompt.length}/{MAX_LENGTH}
              </CharacterCount>
            )}
          </div>
        </PromptActions>
      </PromptFieldWrapper>
    </PromptContainer>
  );
};

export default PromptInput;

